import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { queryKeys, useApiQuery } from '../api/hooks';
import type { AuditPageResponse } from '../api/types';
import { AsyncSection } from '../components/AsyncSection';
import { PageHeader } from '../components/PageHeader';
import { useAuthStore } from '../auth/authStore';
import { clinicDay, clinicTime } from './scheduling/clinicTime';

const PAGE_SIZE = 25;

/**
 * The audit viewer (FR-1103, DEC-048).
 *
 * One component serves both logs, because they are the same table read with a different clinic
 * bound — the clinic's own history, and the platform's record of what it did to clinics. The
 * distinction is enforced by the API; here it is only which path is fetched and which words are
 * shown.
 *
 * The sequence number is displayed rather than hidden as an implementation detail: it is the
 * chain's own ordering, and a gap in it is exactly how a clinic would notice that something had
 * been removed.
 */
export function AuditPage({ scope = 'clinic' }: { readonly scope?: 'clinic' | 'platform' } = {}) {
  const { t } = useTranslation();
  const clinicId = useAuthStore((state) => state.activeClinicId);

  const [action, setAction] = useState('');
  const [page, setPage] = useState(0);

  const search = new URLSearchParams({ page: String(page), size: String(PAGE_SIZE) });
  if (action) {
    search.set('action', action);
  }
  const query = search.toString();

  const isPlatform = scope === 'platform';
  const path = isPlatform ? `/platform/audit?${query}` : `/clinics/${clinicId}/audit?${query}`;

  const entries = useApiQuery<AuditPageResponse>(
    isPlatform ? queryKeys.platformAudit(query) : queryKeys.audit(clinicId ?? '', query),
    path,
    { enabled: isPlatform || Boolean(clinicId) },
  );

  if (!isPlatform && !clinicId) {
    return <PageHeader title={t('nav.items.audit')} description={t('home.noClinic')} />;
  }

  return (
    <>
      <PageHeader
        title={isPlatform ? t('audit.platformTitle') : t('nav.items.audit')}
        description={isPlatform ? t('audit.platformDescription') : t('audit.description')}
      />

      <div className="toolbar">
        <label className="field">
          <span className="field__label">{t('audit.action')}</span>
          <input
            className="field__input ltr"
            value={action}
            placeholder="BRANCH_ACTIVATED"
            onChange={(event) => {
              setAction(event.target.value.toUpperCase());
              setPage(0);
            }}
          />
          <span className="field__hint">{t('audit.actionHint')}</span>
        </label>
      </div>

      <AsyncSection
        query={entries}
        isEmpty={(data) => (data.entries?.length ?? 0) === 0}
        emptyTitle={t('audit.emptyTitle')}
        emptyDescription={t('audit.emptyDescription')}
      >
        {(data) => (
          <>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">#</th>
                    <th scope="col">{t('audit.when')}</th>
                    <th scope="col">{t('audit.action')}</th>
                    <th scope="col">{t('audit.actor')}</th>
                    <th scope="col">{t('audit.entity')}</th>
                    <th scope="col">{t('audit.detail')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.entries ?? []).map((entry) => (
                    <tr key={entry.id}>
                      <th scope="row" className="numeric">
                        {entry.sequenceNo}
                      </th>
                      <td className="numeric">{formatInstant(entry.occurredAt)}</td>
                      <td className="ltr">{entry.action}</td>
                      <td className="ltr">{entry.actorLabel ?? entry.actorKind}</td>
                      <td className="ltr">{entry.entityType ?? '—'}</td>
                      <td>
                        <code className="ltr">{summarise(entry.payload)}</code>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="toolbar" style={{ marginBlockStart: 'var(--space-4)' }}>
              <button
                type="button"
                className="button button--ghost"
                disabled={page === 0}
                onClick={() => setPage((current) => Math.max(0, current - 1))}
              >
                {t('audit.previous')}
              </button>
              <span>
                {t('audit.pageOf', { page: (data.page ?? 0) + 1, total: Math.max(data.totalPages ?? 1, 1) })}
              </span>
              <button
                type="button"
                className="button button--ghost"
                disabled={(data.page ?? 0) + 1 >= (data.totalPages ?? 1)}
                onClick={() => setPage((current) => current + 1)}
              >
                {t('audit.next')}
              </button>
              <span className="field__hint">{t('audit.total', { n: data.totalEntries ?? 0 })}</span>
            </div>
          </>
        )}
      </AsyncSection>
    </>
  );
}

/**
 * An audit entry's instant, in the clinic's own zone.
 *
 * It used to slice the ISO string, which reads it in UTC: Cairo is two or three hours ahead, so
 * anything logged after ten in the evening was filed on screen under the previous day — the exact
 * trap `clinicTime.ts` was written to close. The seconds went with it; nothing in an audit trail is
 * decided by them, and they were three characters of noise on every row.
 */
function formatInstant(value: string | undefined): string {
  if (!value) {
    return '—';
  }
  return `${clinicDay(value)} ${clinicTime(value)}`;
}

/** A one-line rendering of the payload; the full object is one API call away for anyone who needs it. */
function summarise(payload: unknown): string {
  if (payload === null || payload === undefined) {
    return '—';
  }
  if (typeof payload !== 'object') {
    return String(payload);
  }
  const entries = Object.entries(payload as Record<string, unknown>);
  if (entries.length === 0) {
    return '—';
  }
  return entries.map(([key, value]) => `${key}=${String(value)}`).join(' · ');
}
